import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from 'react';
import { GoogleMap, MarkerF, useGoogleMap } from '@react-google-maps/api';
import { useGoogleMaps } from './GoogleMapsContext';
import {formatLocationDisplay} from '../../utils/locationDisplay';

const toFloat = (n) => parseFloat(parseFloat(n).toFixed(8));

const getMarkerLabel = (index) => {
    let value = index + 1;
    let label = '';

    while (value > 0) {
        value -= 1;
        label = String.fromCharCode(65 + (value % 26)) + label;
        value = Math.floor(value / 26);
    }

    return label;
};

const getPointKey = (point) => `${toFloat(point.latitude)}:${toFloat(point.longitude)}`;

const hasValidCoordinates = (point) => {
    const latitude = toFloat(point.latitude);
    const longitude = toFloat(point.longitude);

    return Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180;
};

const detachDirectionsRenderer = (renderer) => {
    if (!renderer) return;

    renderer.setOptions?.({suppressPolylines: true, suppressMarkers: true});
    renderer.setPanel?.(null);
    renderer.setDirections?.(null);
    renderer.setMap?.(null);
    renderer.unbindAll?.();
    window.google?.maps?.event?.clearInstanceListeners?.(renderer);
};

const NativeDirectionsRenderer = ({directions}) => {
    const map = useGoogleMap();

    useLayoutEffect(() => {
        if (!map || !directions) return undefined;

        const renderer = new window.google.maps.DirectionsRenderer({
            suppressMarkers: true,
        });
        renderer.setDirections(directions);
        renderer.setMap(map);

        return () => {
            detachDirectionsRenderer(renderer);
        };
    }, [directions, map]);

    return null;
};

const NPMapViewDirections = ({ origins = [], dropoffs = [], onWaypointsOptimized, onDistanceCalculated }) => {
    const { isLoaded, loadError } = useGoogleMaps();
    const [directionsResponse, setDirectionsResponse] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 49.8951, lng: -97.1384 }); // Winnipeg

    const directionsServiceRef = useRef(null);
    const directionsRequestIdRef = useRef(0);
    const onWaypointsOptimizedRef = useRef(onWaypointsOptimized);
    const onDistanceCalculatedRef = useRef(onDistanceCalculated);
    onWaypointsOptimizedRef.current = onWaypointsOptimized;
    onDistanceCalculatedRef.current = onDistanceCalculated;

    // Keep routing values raw while deriving a separate marker display label.
    const mapOrigins = useMemo(
        () => origins
            .map(o => ({
                latitude: o.latitude,
                longitude: o.longitude,
                address: o.address,
                displayAddress: formatLocationDisplay(o),
            }))
            .filter(hasValidCoordinates),
        [origins]
    );

    const mapDropoffs = useMemo(
        () => dropoffs
            .map(d => ({
                latitude: d.latitude,
                longitude: d.longitude,
                address: d.address,
                displayAddress: formatLocationDisplay(d),
            }))
            .filter(hasValidCoordinates),
        [dropoffs]
    );
    const routePoints = useMemo(
        () => [...mapOrigins, ...mapDropoffs],
        [mapOrigins, mapDropoffs]
    );
    const routeRequestKey = useMemo(() => JSON.stringify({
        dropoffCount: dropoffs.length,
        dropoffs: mapDropoffs.map((point) => ({
            latitude: toFloat(point.latitude),
            longitude: toFloat(point.longitude),
        })),
        originCount: origins.length,
        origins: mapOrigins.map((point) => ({
            latitude: toFloat(point.latitude),
            longitude: toFloat(point.longitude),
        })),
    }), [dropoffs.length, mapDropoffs, mapOrigins, origins.length]);
    const visibleDirections = directionsResponse?.requestKey === routeRequestKey
        ? directionsResponse.result
        : null;

    const markerPoints = useMemo(() => {
        const seen = new Set();

        return routePoints.filter((point) => {
            const key = getPointKey(point);

            if (seen.has(key)) {
                return false;
            }

            seen.add(key);
            return true;
        });
    }, [routePoints]);

    // Initialize DirectionsService once
    useEffect(() => {
        if (!isLoaded) return;
        if (!directionsServiceRef.current) {
            directionsServiceRef.current = new window.google.maps.DirectionsService();
        }
    }, [isLoaded]);

    useEffect(() => {
        if (!isLoaded) return undefined;

        const requestId = directionsRequestIdRef.current + 1;
        directionsRequestIdRef.current = requestId;
        const invalidateRequest = () => {
            if (directionsRequestIdRef.current === requestId) {
                directionsRequestIdRef.current += 1;
            }
        };

        setDirectionsResponse(null);
        const routeRequest = JSON.parse(routeRequestKey);

        const hasCompleteRoute = routeRequest.origins.length > 0 &&
            routeRequest.dropoffs.length > 0 &&
            routeRequest.origins.length === routeRequest.originCount &&
            routeRequest.dropoffs.length === routeRequest.dropoffCount;

        if (!hasCompleteRoute) {
            return invalidateRequest;
        }

        const directionsService = directionsServiceRef.current;
        if (!directionsService) return invalidateRequest;

        // Ensure valid start and end points
        const requestRoutePoints = [...routeRequest.origins, ...routeRequest.dropoffs];
        const originPoint = requestRoutePoints[0];
        const destinationPoint = requestRoutePoints[requestRoutePoints.length - 1];

        if (!originPoint || !destinationPoint) return invalidateRequest;

        const waypointPoints = requestRoutePoints.slice(1, -1);
        const waypoints = waypointPoints.map((point) => ({
            location: new window.google.maps.LatLng(toFloat(point.latitude), toFloat(point.longitude)),
            stopover: true,
        }));

        const request = {
            origin: new window.google.maps.LatLng(toFloat(originPoint.latitude), toFloat(originPoint.longitude)),
            destination: new window.google.maps.LatLng(toFloat(destinationPoint.latitude), toFloat(destinationPoint.longitude)),
            waypoints: waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
        };

        directionsService.route(request, (result, status) => {
            if (directionsRequestIdRef.current !== requestId) return;

            if (status === window.google.maps.DirectionsStatus.OK) {
                setDirectionsResponse({requestKey: routeRequestKey, result});

                if (onWaypointsOptimizedRef.current) {
                    const waypointOrder = result.routes[0].waypoint_order || [];
                    const orderedWaypointPoints = waypointOrder.length > 0
                        ? waypointOrder.map((index) => waypointPoints[index])
                        : waypointPoints;
                    const orderedRoutePoints = [originPoint, ...orderedWaypointPoints, destinationPoint];

                    onWaypointsOptimizedRef.current(orderedRoutePoints.map((point) => ({
                        location: new window.google.maps.LatLng(toFloat(point.latitude), toFloat(point.longitude)),
                    })));
                }

                if (onDistanceCalculatedRef.current) {
                    const totalDistance = result.routes[0].legs.reduce((acc, leg) => acc + leg.distance.value, 0);
                    const distanceInKm = totalDistance / 1000;
                    onDistanceCalculatedRef.current(distanceInKm);
                }

                setMapCenter({
                    lat: toFloat(originPoint.latitude),
                    lng: toFloat(originPoint.longitude),
                });
            } else {
                console.error("Error fetching directions:", status);
            }
        });

        return invalidateRequest;
    }, [isLoaded, routeRequestKey]);

    useEffect(() => {
        if (markerPoints.length !== 1) return;

        const point = markerPoints[0];
        setMapCenter({
            lat: toFloat(point.latitude),
            lng: toFloat(point.longitude),
        });
    }, [markerPoints]);

    if (loadError) {
        return <div>Error loading maps</div>;
    }

    if (!isLoaded) {
        return <div>Loading...</div>;
    }

    return (
        <GoogleMap
            mapContainerStyle={{ width: '100%', height: '400px' }}
            center={mapCenter}
            zoom={13}
        >
            <NativeDirectionsRenderer directions={visibleDirections} />
            {markerPoints.map((point, index) => (
                <MarkerF
                    key={`${point.latitude}-${point.longitude}-${index}`}
                    position={{ lat: toFloat(point.latitude), lng: toFloat(point.longitude) }}
                    label={{
                        text: getMarkerLabel(index),
                        color: '#f3f4f6',
                        fontSize: '16px',
                        fontWeight: '700',
                    }}
                    title={point.displayAddress || point.address}
                />
            ))}
        </GoogleMap>
    );
};

export default NPMapViewDirections;
